import React, { useState, FC, useEffect } from 'react';

import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Typography
} from '@material-ui/core';
import { LayoutVariant } from '../Core/CommonTypes';
import StatusTypeList from './StatusTypeList';
import {
  DSOrder,
  ItemCount,
  KitchenBoardType,
  StatusBoardType
} from './StatusTypeList.stories';
import { KitchenDisplay } from 'kiolyn-api';
import OrderCard from './OrderCard';
import { isEmpty } from 'lodash';
import { grey } from '@material-ui/core/colors';
import OrderCardDialog from './OrderCardDialog';
import { isNullOrUndefined } from 'util';
import { useKitchenDisplay } from './KitchenDisplayStore';

const useStyles = makeStyles(theme => ({
  statusTypesStyle: {
    width: '100%',
    marginBottom: 0,
    flexWrap: 'nowrap',
    height: theme.spacing(7),
    paddingTop: 4
  },
  mainContainer: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(1),
    margin: theme.spacing(1)
  },
  orderingContainer: {
    height: '100%',
    flexWrap: 'nowrap'
  },
  orderGrid: {
    marginTop: theme.spacing(1)
  },
  orderContainer: {
    padding: 10
  },
  orderPreparingDivider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  itemList: {
    padding: theme.spacing(2),
    backgroundColor: grey[50],
    width: theme.spacing(42),
    flex: '1 1 auto'
  },
  name: {
    textAlign: 'left',
    margin: 5,
    lineHeight: 1.2,
    alignSelf: 'left'
  },
  nameFull: {
    width: 150,
    textAlign: 'left',
    flexGrow: 1,
    margin: 5,
    lineHeight: 1.2,
    justifyContent: 'left',
    alignSelf: 'flex-start'
  },
  itemButton: {
    width: '100%'
  },
  selectedItemButton: {
    width: '100%',
    background: 'orange',
    animationName: '$blinker',
    animationDuration: '1s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite'
  },
  '@keyframes blinker': {
    from: { opacity: 1 },
    to: { opacity: 0 }
  }
}));

interface KitchenDisplayInteractionProps {
  variant?: LayoutVariant;
  kitchenDisplay?: KitchenDisplay;
  orders?: DSOrder[];
  onStatusChanged?: (order: DSOrder) => void;
}

const KitchenDisplayInteraction: FC<KitchenDisplayInteractionProps> = ({
  variant,
  kitchenDisplay,
  orders,
  onStatusChanged
}: KitchenDisplayInteractionProps) => {
  const classes = useStyles();
  const kitchenDisplayStore = useKitchenDisplay();

  const handleCloseOrderView = () => {
    setShowOrderCardDialog(false);
  };

  let statusBoardTypes: StatusBoardType[] = [
    {
      type: KitchenBoardType.PREPARING,
      name: kitchenDisplay?.preparing_text || 'Preparing (Kitchen)'
    }
  ];

  if (kitchenDisplay?.is_ready_runner_view === true) {
    statusBoardTypes.push({
      type: KitchenBoardType.READY,
      name: kitchenDisplay?.ready_text || 'Ready (Runner)'
    });
  }

  if (kitchenDisplay?.is_done_view === true) {
    statusBoardTypes.push({ type: KitchenBoardType.DONE, name: 'Done' });
  }

  const getItemList = (orders: DSOrder[] | undefined): ItemCount[] => {
    let itemList: ItemCount[] = [];
    orders?.forEach(o => {
      o.items?.forEach(i => {
        if (!(i.kitchen_checked === true || i.kitchen_ready === true)) {
          let item = itemList.find(t => t.id === i.itemid);
          if (item) {
            item.count += i.count ?? 0;
          } else {
            item = {
              id: i.itemid ?? i.id,
              name: i.name,
              count: i.count ?? 0,
              selected: false
            };
            itemList.push(item);
          }
        }
      });
    });

    return itemList;
  };

  let initialItemList = getItemList(orders);
  let initialOrder: DSOrder = {
    id: '',
    type: '',
    name: '',
    area: '',
    area_name: '',
    items: [],
    table_name: '',
    customer_name: '',
    persons: 0
  };

  const [state, setState] = useState({
    itemList: initialItemList,
    statusType: statusBoardTypes[0],
    statusTypes: statusBoardTypes,
    orders: orders,
    displayOrders: orders,
    readyOrders: orders,
    currentOrder: initialOrder,
    hasPrevious: false,
    hasNext: false
  });

  useEffect(() => {
    let itemList = getItemList(orders);

    let displayOrders = getDisplayOrders(orders, state.statusType);
    let readyOrders = getReadyOrders(orders, state.statusType);

    setState({
      ...state,
      itemList: itemList,
      orders: orders,
      displayOrders: displayOrders,
      readyOrders: readyOrders
    });
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  function getDisplayOrders(
    orders: DSOrder[] | undefined,
    statusType: StatusBoardType
  ) {
    let displayOrders = kitchenDisplayStore.sortKitchenOrders(orders);

    if (statusType.type === KitchenBoardType.PREPARING) {
      displayOrders = displayOrders?.filter(
        i =>
          !isEmpty(i.items) &&
          i.items.length > 0 &&
          i.items.findIndex(t => !t.kitchen_ready) >= 0
      );
    } else if (statusType.type === KitchenBoardType.READY) {
      displayOrders = displayOrders?.filter(
        i =>
          !isEmpty(i.items) &&
          i.items.length > 0 &&
          i.items.findIndex(t => !t.kitchen_checked) >= 0
      );
    } else if (statusType.type === KitchenBoardType.DONE) {
      displayOrders = displayOrders?.filter(
        i =>
          !isEmpty(i.items) &&
          i.items.length > 0 &&
          i.items.findIndex(t => !t.kitchen_checked) < 0
      );
    }
    return displayOrders;
  }

  function getReadyOrders(
    orders: DSOrder[] | undefined,
    statusType: StatusBoardType
  ) {
    let displayOrders = kitchenDisplayStore.sortKitchenOrders(orders);

    if (statusType.type === KitchenBoardType.PREPARING) {
      displayOrders = displayOrders?.filter(
        i =>
          !isEmpty(i.items) &&
          i.items.length > 0 &&
          i.items.findIndex(t => !t.kitchen_ready) < 0
      );
    } else if (statusType.type === KitchenBoardType.READY) {
      displayOrders = displayOrders?.filter(
        i =>
          !isEmpty(i.items) &&
          i.items.length > 0 &&
          i.items.findIndex(t => !t.kitchen_checked) < 0
      );
    }
    return displayOrders;
  }

  const handleStatusTypeChange = (type: StatusBoardType) => {
    let displayOrders = getDisplayOrders(orders, type);
    let readyOrders = getReadyOrders(orders, type);

    setState({
      ...state,
      statusType: type,
      displayOrders: displayOrders,
      readyOrders: readyOrders
    });
  };

  const onItemSelected = (item: ItemCount) => {
    let items = state.itemList;
    items.forEach(i => {
      i.selected = item.id === i.id ? !i.selected : false;
    });

    let allOrders = state.orders;
    allOrders?.forEach(o => {
      o.items?.forEach(i => {
        i.kitchen_selected = i.itemid === item.id ? item.selected : false;
      });
    });

    let displayOrders = getDisplayOrders(orders, state.statusType);
    let readyOrders = getReadyOrders(orders, state.statusType);
    setState({
      ...state,
      itemList: items,
      orders: allOrders,
      displayOrders: displayOrders,
      readyOrders: readyOrders
    });
  };
  const handleSelectItem = (item: ItemCount) => {
    onItemSelected(item);

    setTimeout(() => {
      onItemSelected(item);
    }, 3000);
  };

  const [showOrderCardDialog, setShowOrderCardDialog] = useState(false);

  const handleShowOrderView = (order: DSOrder) => {
    if (isNullOrUndefined(orders) || orders.length === 0) return;
    let currentIndex = orders.findIndex(t => t.id === order.id);

    setState({
      ...state,
      currentOrder: order,
      hasNext: currentIndex < orders.length - 1,
      hasPrevious: currentIndex > 0
    });
    setShowOrderCardDialog(true);
  };

  const handleNavigation = (index: number) => {
    if (index === 0 || isNullOrUndefined(orders) || orders.length === 0) return;

    let currentIndex = orders.findIndex(t => t.id === state.currentOrder.id);
    if (index > 0 && currentIndex < orders.length - 1) {
      let currentOrder = orders[currentIndex + 1];
      let hasNext = currentIndex + 1 < orders.length - 1;
      setState({
        ...state,
        currentOrder: currentOrder,
        hasNext: hasNext,
        hasPrevious: true
      });
    } else if (index < 0 && currentIndex > 0) {
      let currentOrder = orders[currentIndex - 1];
      let hasPrevious = currentIndex - 1 > 0;
      setState({
        ...state,
        currentOrder: currentOrder,
        hasNext: true,
        hasPrevious: hasPrevious
      });
    } else {
      setState({
        ...state,
        hasNext: currentIndex < orders.length - 1,
        hasPrevious: currentIndex > 0
      });
    }
  };

  return (
    <Container className={classes.mainContainer}>
      <Grid container className={classes.orderingContainer}>
        {!kitchenDisplay?.hide_summary && (
          <Paper className={classes.itemList}>
            {state.itemList?.map(item => (
              <Button
                key={'button' + item.id}
                onClick={() => handleSelectItem(item)}
                size="small"
                color="primary"
                className={
                  item.selected
                    ? classes.selectedItemButton
                    : classes.itemButton
                }
              >
                <Grid container item key={item.id} direction="row">
                  <Typography variant="h6" className={classes.nameFull}>
                    {item.name}
                  </Typography>
                  <Typography
                    variant="h6"
                    className={classes.name}
                    align="right"
                  >
                    {item.count}
                  </Typography>
                </Grid>
              </Button>
            ))}
          </Paper>
        )}
        <Grid
          container
          key="orderGrid"
          direction="column"
          className={classes.orderGrid}
        >
          <Grid
            container
            direction="row"
            justify="space-between"
            className={classes.statusTypesStyle}
          >
            <StatusTypeList
              statusType={state.statusType}
              statusTypes={state.statusTypes}
              onStatusTypeChange={handleStatusTypeChange}
              variant={variant}
            />
          </Grid>
          <Divider></Divider>
          <Box>
            <Grid container spacing={2} className={classes.orderContainer}>
              {state.displayOrders?.map(order => (
                <Grid item key={order.id}>
                  <OrderCard
                    statusType={state.statusType}
                    order={order}
                    onStatusChanged={onStatusChanged}
                    onShowOrderView={handleShowOrderView}
                  />
                </Grid>
              ))}
            </Grid>
            {(state.statusType.type === KitchenBoardType.PREPARING ||
              state.statusType.type === KitchenBoardType.READY) && (
              <>
                <Divider className={classes.orderPreparingDivider}></Divider>
                <Grid container spacing={2} className={classes.orderContainer}>
                  {state.readyOrders?.map(order => (
                    <Grid item key={order.id}>
                      <OrderCard
                        statusType={state.statusType}
                        order={order}
                        onStatusChanged={onStatusChanged}
                        onShowOrderView={handleShowOrderView}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
          {showOrderCardDialog && (
            <OrderCardDialog
              statusType={state.statusType}
              order={state.currentOrder}
              onStatusChanged={onStatusChanged}
              onClose={handleCloseOrderView}
              hasPrevious={state.hasPrevious}
              hasNext={state.hasNext}
              onNavigation={handleNavigation}
            ></OrderCardDialog>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default KitchenDisplayInteraction;
